import { createTheme } from '@mui/material/styles';

export type AppThemeMode = 'light' | 'dark';

// DouBoard 2.0: energetic marker pink with accessible supporting surfaces.
// pink/pinkLight are deliberately light: some legacy controls use dark text on these fills.
export const BRAND = {
  pink: '#FFB5D2',
  pinkLight: '#FFE4EE',
  pinkDark: '#F589B6',
  pinkStrong: '#E61E73',
  pinkStrongDark: '#C91863',
  ink: '#171A22',
  inkSoft: '#3B4252',
  pageBackground: '#FCFAF4',
  darkBackground: '#141821',
  darkPanel: '#1B2130',
  darkBorder: '#2B3447',
  stickyYellow: '#FFE58F',
  stickyMint: '#CFF4D2',
  stickySky: '#D9ECFF',
  stickyLavender: '#E6DDFF'
} as const;

// Soft marker-pink wash used for selection and hover states.
export function brandTint(alpha: number) {
  return `rgba(230, 30, 115, ${Math.min(1, Math.max(0, alpha))})`;
}

// Kept for consumers that need a solid backdrop rather than the CSS board pattern.
export const BRAND_PAGE_GRADIENT = 'linear-gradient(145deg, #FCFAF4 0%, #FFF3F8 100%)';

/** High-contrast action treatment for board commands that commit a change. */
export const BOARD_PRIMARY_ACTION_SX = {
  backgroundColor: BRAND.pinkStrongDark,
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#A50F4D',
    color: '#ffffff'
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(201, 24, 99, 0.32)',
    color: 'rgba(255, 255, 255, 0.8)'
  }
} as const;

/** AI gets the expressive marker gradient; regular board actions stay solid pink. */
export const BOARD_AI_GRADIENT_SX = {
  backgroundColor: BRAND.pinkStrongDark,
  backgroundImage: 'linear-gradient(115deg, #E61E73 0%, #C91863 58%, #A50F4D 100%)',
  backgroundSize: '175% 175%',
  backgroundPosition: '0% 50%',
  color: '#ffffff',
  boxShadow: '0 6px 17px rgba(201, 24, 99, 0.28)',
  transition: 'background-position 180ms ease, box-shadow 180ms ease, transform 180ms ease',
  '&:hover': {
    backgroundImage: 'linear-gradient(115deg, #E61E73 0%, #C91863 58%, #A50F4D 100%)',
    backgroundPosition: '100% 50%',
    boxShadow: '0 9px 20px rgba(201, 24, 99, 0.36)',
    color: '#ffffff'
  },
  '& .MuiButton-startIcon': { color: '#ffffff' },
  '&.Mui-disabled': {
    backgroundImage: 'none',
    backgroundColor: 'rgba(201, 24, 99, 0.32)',
    color: 'rgba(255, 255, 255, 0.8)'
  }
} as const;

export function createAppTheme(mode: AppThemeMode) {
  const isDark = mode === 'dark';

  return createTheme({
    cssVariables: true,
    palette: {
      mode,
      primary: {
        main: BRAND.pinkStrongDark,
        light: BRAND.pinkStrong,
        dark: '#A50F4D',
        contrastText: '#ffffff'
      },
      // Pink + black pairing: black is the supporting brand colour.
      secondary: {
        main: BRAND.ink,
        light: BRAND.inkSoft,
        dark: '#000000',
        contrastText: '#ffffff'
      },
      background: {
        default: isDark ? BRAND.darkBackground : BRAND.pageBackground,
        paper: isDark ? BRAND.darkPanel : '#ffffff'
      }
    },
    shape: {
      borderRadius: 14
    },
    typography: {
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      h4: {
        fontWeight: 700
      },
      button: {
        textTransform: 'none',
        fontWeight: 600
      }
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true
        },
        styleOverrides: {
          root: {
            variants: [
              // Pastel pink text/borders are unreadable on white; use the strong pink instead.
              {
                props: { variant: 'text', color: 'primary' },
                style: { color: BRAND.pinkStrongDark }
              },
              {
                props: { variant: 'outlined', color: 'primary' },
                style: { color: BRAND.pinkStrongDark, borderColor: BRAND.pinkStrongDark }
              }
            ]
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: BRAND.pinkStrong
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: BRAND.pinkStrong
            }
          }
        }
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: BRAND.pinkStrong
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              color: BRAND.pinkStrong
            }
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: BRAND.pinkStrong
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          fullWidth: true
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            border: isDark ? '1px solid rgba(255, 181, 210, 0.2)' : `1px solid ${brandTint(0.15)}`
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: isDark ? '#64748b' : '#94a3b8',
            '&.Mui-checked': {
              color: BRAND.pinkStrong
            },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: BRAND.pink,
              opacity: 1
            }
          },
          track: {
            backgroundColor: isDark ? '#334155' : '#cbd5e1',
            opacity: 1,
            borderRadius: 999
          },
          thumb: {
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.18)'
          }
        }
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          grouped: {
            borderColor: isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.28)'
          }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: isDark ? '#cbd5e1' : '#334155',
            borderColor: isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.28)',
            '&.Mui-selected': {
              color: BRAND.ink,
              backgroundColor: BRAND.pinkLight
            },
            '&.Mui-selected:hover': {
              backgroundColor: BRAND.pink
            }
          }
        }
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true,
          // Tooltips must never swallow clicks meant for the menu items they overlap
          // (e.g. the "Shapes" tooltip sits on top of the first item of the shape menu).
          disableInteractive: true,
          enterDelay: 120,
          enterNextDelay: 60
        },
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? 'rgba(7, 11, 20, 0.96)' : 'rgba(17, 24, 39, 0.96)',
            color: '#f8fafc',
            fontSize: '0.72rem',
            fontWeight: 700,
            lineHeight: 1.2,
            padding: '8px 10px',
            borderRadius: 10,
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.22)',
            border: '1px solid rgba(148, 163, 184, 0.18)'
          },
          arrow: {
            color: isDark ? 'rgba(7, 11, 20, 0.96)' : 'rgba(17, 24, 39, 0.96)',
            '&::before': {
              boxSizing: 'border-box',
              border: '1px solid rgba(148, 163, 184, 0.18)',
              boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)'
            }
          }
        }
      }
    }
  });
}
